/**
 * Automatic 7-day key rotation.
 *
 * Design:
 *   - Every 7 days we mint a fresh RSA-OAEP + Kyber-768 keypair
 *     on-device and upload the new public material to the server.
 *   - The Dilithium identity key stays STABLE — it signs every new
 *     bundle so peers can verify "yes, this is still the same user
 *     rotating, not a server-side swap." Rotating identity keys
 *     is tracked separately in DEFERRED_IMPROVEMENTS.md (needs OOB
 *     continuity proofs).
 *   - Old private keys are moved into a client-only archive ring so
 *     ciphertext wrapped against a previous pubkey still decrypts
 *     locally. The ring is capped at `MAX_ARCHIVE_ENTRIES` ≈ 6 weeks
 *     at the default 7-day cadence; older entries fall off and their
 *     messages become permanently undecryptable on this device
 *     (matches the 24h message TTL, so this is almost never user-
 *     visible — the archive mostly protects against clock skew and
 *     replay of imported history blobs).
 *
 * Threat model covered:
 *   - Long-term key compromise: an attacker who steals yesterday's
 *     private key via local malware can still read yesterday's
 *     traffic, but cannot read anything from >7 days earlier and
 *     cannot read anything from the current rotation window.
 *   - Cryptographic fatigue: even without a compromise, forcing
 *     fresh keys keeps the long-running attack surface bounded.
 *   - Silent server-side key swap: because every new bundle is
 *     Dilithium-signed by the STABLE identity key, peers can detect
 *     any rotation that didn't come from the legitimate user. The
 *     server cannot forge a rotation.
 *
 * Known v1 gaps (deferred):
 *   - Dilithium identity rotation (chain-signed continuity proof).
 *   - Per-conversation ratchet — we're still long-term-keys-wrapping
 *     per-message AES keys. Moving to a double-ratchet would shrink
 *     the exposure window to minutes instead of days.
 */
import axios from 'axios';
import {
  generateKyberKeypair,
  signBundle,
  b64,
} from './pqCrypto';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Storage key names. Keep the `ghostcomm_` prefix so the panic
// button wipes them along with everything else.
const CURRENT_KEYS = 'ghostcomm_keys';
const CURRENT_PQ_KEYS = 'ghostcomm_pq_keys';
const ARCHIVE_KEYS = 'ghostcomm_keys_archive';
const ARCHIVE_PQ_KEYS = 'ghostcomm_pq_keys_archive';
const LAST_ROTATION_AT = 'ghostcomm_last_rotation_at';
// User-configurable cadence override. Falls back to DEFAULT_ROTATION_DAYS
// when unset or invalid. Bounded by MIN/MAX so a caller can't push the
// cadence into hostile territory (1d would thrash, 365d defeats the
// point of rotation entirely).
const CADENCE_OVERRIDE_KEY = 'ghostcomm_rotation_interval_days';
const DEFAULT_ROTATION_DAYS = 7;
export const MIN_ROTATION_DAYS = 1;
export const MAX_ROTATION_DAYS = 30;

const _day = 24 * 60 * 60 * 1000;

/**
 * Effective rotation cadence in days — reads the optional user
 * override from localStorage, clamps to [MIN, MAX], falls back to
 * the 7-day default if the override is missing or malformed.
 */
export const getRotationIntervalDays = () => {
  try {
    const raw = localStorage.getItem(CADENCE_OVERRIDE_KEY);
    if (!raw) return DEFAULT_ROTATION_DAYS;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return DEFAULT_ROTATION_DAYS;
    return Math.max(MIN_ROTATION_DAYS, Math.min(MAX_ROTATION_DAYS, Math.round(n)));
  } catch {
    return DEFAULT_ROTATION_DAYS;
  }
};

/**
 * Store the user's preferred cadence. Pass `null` to revert to
 * the default. Returns the effective value actually stored after
 * clamping so callers can update their UI to the normalized value.
 */
export const setRotationIntervalDays = (days) => {
  try {
    if (days === null || days === undefined) {
      localStorage.removeItem(CADENCE_OVERRIDE_KEY);
      return DEFAULT_ROTATION_DAYS;
    }
    const clamped = Math.max(MIN_ROTATION_DAYS, Math.min(MAX_ROTATION_DAYS, Math.round(days)));
    localStorage.setItem(CADENCE_OVERRIDE_KEY, String(clamped));
    return clamped;
  } catch {
    return getRotationIntervalDays();
  }
};

/**
 * Computed in-ms interval. Not frozen at module load so callers
 * who change the cadence in Settings pick up the new value on the
 * NEXT `shouldRotate()` check without a page reload.
 */
export const getRotationIntervalMs = () => getRotationIntervalDays() * _day;

// Back-compat export for callers that already reference the
// constant. Still reads the current stored cadence at access time
// because it's a getter, not a frozen value.
export const ROTATION_INTERVAL_MS = getRotationIntervalMs();

// Keep ~6 weeks of history on-device. At the 7-day cadence this is
// six rotations, which bounds localStorage growth and comfortably
// covers the 24h message TTL.
const MAX_ARCHIVE_ENTRIES = 6;

// ─── Helpers ────────────────────────────────────────────────────────

const _now = () => Date.now();

const _readJson = (key) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
};

const _writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private-mode localStorage / quota exhausted — swallow, since
    // the archive is a best-effort cache and a failure here simply
    // means old messages can't be decrypted. That's acceptable.
  }
};

const _pushArchive = (key, entry) => {
  const arr = _readJson(key) || [];
  arr.push(entry);
  while (arr.length > MAX_ARCHIVE_ENTRIES) arr.shift();
  _writeJson(key, arr);
};

/**
 * Decide whether rotation should run. Pure function — no side
 * effects — so the caller can decide on policy (e.g. "don't rotate
 * in duress mode") before invoking `rotateKeys()`.
 */
export const shouldRotate = () => {
  const raw = localStorage.getItem(LAST_ROTATION_AT);
  const last = raw ? Number(raw) : 0;
  if (!last || Number.isNaN(last)) return true;
  return _now() - last >= getRotationIntervalMs();
};

export const lastRotationAt = () => {
  const raw = localStorage.getItem(LAST_ROTATION_AT);
  const n = raw ? Number(raw) : 0;
  return n && !Number.isNaN(n) ? n : null;
};

/**
 * Return every {rsa, kyber} private-key pair we have locally —
 * current first, archived in reverse chronological order. The
 * decrypt fallback walks this list.
 */
export const loadKeyRing = async () => {
  const ring = [];
  const cur = _readJson(CURRENT_KEYS);
  const curPq = _readJson(CURRENT_PQ_KEYS);
  if (cur) ring.push({ classical: cur, pq: curPq, current: true });
  const arch = _readJson(ARCHIVE_KEYS) || [];
  const archPq = _readJson(ARCHIVE_PQ_KEYS) || [];
  // Newest-first so decrypt tries recent keys before old ones.
  for (let i = arch.length - 1; i >= 0; i--) {
    ring.push({
      classical: arch[i],
      pq: archPq[i] || null,
      current: false,
    });
  }
  return ring;
};

/**
 * Import a JWK private key lazily. Returns a CryptoKey suitable for
 * RSA-OAEP decrypt.
 */
export const importRsaPrivate = async (jwk) => {
  if (!jwk) return null;
  return window.crypto.subtle.importKey(
    'jwk', jwk, { name: 'RSA-OAEP', hash: 'SHA-256' }, true, ['decrypt']
  );
};

/**
 * Actually rotate.
 *
 * Params:
 *   user            — the authenticated user (needs `.id`).
 *   currentDilithiumSecret — the stable identity private key bytes.
 *   currentPublicKeyJwk   — the CURRENT RSA pub JWK (pre-rotation).
 *   currentKyberPub       — the CURRENT Kyber pub bytes (pre-rotation).
 *
 * Returns the NEW key material so the caller can refresh React state.
 * Throws on unrecoverable errors so the caller can toast + retry
 * later (we never proceed half-rotated).
 */
export const rotateKeys = async ({ user, dilithiumPub, dilithiumSecret }) => {
  if (!user?.id) throw new Error('Cannot rotate without an authenticated user');
  if (!dilithiumSecret) throw new Error('Dilithium identity key missing');

  // 1. Mint fresh RSA keypair.
  const rsaPair = await window.crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true,
    ['encrypt', 'decrypt'],
  );
  const newPublicKeyJwk = await window.crypto.subtle.exportKey('jwk', rsaPair.publicKey);
  const newPrivateKeyJwk = await window.crypto.subtle.exportKey('jwk', rsaPair.privateKey);

  // 2. Mint fresh Kyber keypair.
  const kem = generateKyberKeypair();
  const newKyberPub = kem.publicKey;
  const newKyberSecret = kem.secretKey;

  // 3. Archive the PREVIOUS current keys before overwriting.
  const prevClassical = _readJson(CURRENT_KEYS);
  const prevPq = _readJson(CURRENT_PQ_KEYS);
  if (prevClassical) _pushArchive(ARCHIVE_KEYS, prevClassical);
  if (prevPq) _pushArchive(ARCHIVE_PQ_KEYS, prevPq);

  // 4. Install new material as current.
  _writeJson(CURRENT_KEYS, {
    publicKey: newPublicKeyJwk,
    privateKey: newPrivateKeyJwk,
  });
  _writeJson(CURRENT_PQ_KEYS, {
    kyber_pub: b64.encode(newKyberPub),
    kyber_secret: b64.encode(newKyberSecret),
    // Dilithium is STABLE across rotations — copy the existing bytes
    // through so the shape of CURRENT_PQ_KEYS is unchanged for
    // existing readers.
    dilithium_pub: b64.encode(dilithiumPub),
    dilithium_secret: b64.encode(dilithiumSecret),
  });

  // 5. Publish to server. Order matters: upload the RSA pub first so
  //    any race-y peer fetch between steps sees coherent state
  //    (kyber-then-rsa could momentarily have a new kyber wrapped to
  //    an old rsa — the reverse order is the benign case).
  const createdAt = new Date().toISOString();
  await axios.put(`${API}/auth/public-key`, {
    public_key: JSON.stringify(newPublicKeyJwk),
  });

  const { kyber_pub_b64, bundle_sig_b64 } = signBundle(
    newPublicKeyJwk,
    newKyberPub,
    user.id,
    dilithiumSecret,
    createdAt,
  );
  await axios.put(`${API}/auth/pq-bundle`, {
    kyber_pub: kyber_pub_b64,
    dilithium_pub: b64.encode(dilithiumPub),
    bundle_sig: bundle_sig_b64,
    created_at: createdAt,
  });

  // 6. Stamp the rotation timestamp last so a partial failure above
  //    leaves the client eligible to retry next time.
  localStorage.setItem(LAST_ROTATION_AT, String(_now()));

  // 7. Return the freshly-minted material in the shape AuthContext
  //    expects for its `cryptoKeys` state.
  return {
    publicKey: rsaPair.publicKey,
    privateKey: rsaPair.privateKey,
    publicKeyJwk: newPublicKeyJwk,
    kyberPub: newKyberPub,
    kyberSecret: newKyberSecret,
  };
};
